import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RouteAccessGate } from '@/components/routes/RouteAccessGate';
import { useFeatures } from '@/core/features/useFeatures';
import { POS_ROUTE_FEATURES } from '@/config/routeFeatures';
import { resolveOrgRoutePermission } from '@/utils/navigation';
import { useAppSelector } from '@/hooks/useAppSelector';
import type { RootState } from '@/store';
import {
  isOnboardingDone,
  isPlatformOnboardingRequired,
  isFattahOneProductSelectionRequired,
  isProductSelectionDone,
} from '@/modules/onboarding/onboardingState';
import { resolveLoginRedirect } from '@/routes/ModuleScopeGuard';
import { isSessionRestorePending } from '@/utils/sessionBootstrap';
import { useActiveModule } from '@/hooks/useActiveModule';
import { getPostLoginPath, isHrEssOnlyUser, HR_ESS_HOME_PATH } from '@/utils/moduleScope';
import { evaluateOrgRouteDenial, evaluatePosRouteDenial } from '@/utils/routeAccessPolicy';
import { getLauncherModules, resolveOwnerHomePath, resolveSingleAppHomePath, isOrgOwner } from '@/utils/moduleAccess';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import { canAccessWorkspacePath } from '@/utils/moduleNavigation';
import { isLockedModulePath } from '@/utils/routeGuardUtils';
import { usePermissions } from '@/hooks/usePermissions';
import { useScreenActions } from '@/hooks/useScreenActions';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByLoginPath } from '@/config/moduleEntries';
import { resolveModuleAdminRoutePermission } from '@/config/moduleDefaults';
import { APP_HOME_FALLBACK } from '@/constants/authRoutes';
import type { ErpModule } from '@/api/platform.api';

type RoleGuard = 'master' | 'org' | 'any';

const POS_ROUTE_PERMISSIONS: Record<string, string | string[] | null> = {
  '/pos': 'BILLING_CREATE',
  '/pos/held': 'BILLING_VIEW',
  '/pos/shift': 'BILLING_CREATE',
  '/pos/returns': 'BILLING_RETURN',
  '/pos/analytics': ['BILLING_VIEW', 'REPORT_VIEW'],
  '/pos/setup': ['POS_ADMIN', 'STORE_MANAGE'],
  '/pos/terminal-reports': 'REPORT_VIEW',
};

interface ProtectedRouteProps {
  role?: RoleGuard;
  permission?: string | string[];
}

type ProtectedRouteOutletProps = ProtectedRouteProps & {
  user: NonNullable<RootState['auth']['user']>;
  entryModule: ErpModule | null | undefined;
};

function ProtectedRouteOutlet({ role = 'any', permission, user, entryModule }: ProtectedRouteOutletProps) {
  const location = useLocation();
  const { hasFeature, loading: featuresLoading } = useFeatures();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const { has } = usePermissions();
  const screenActions = useScreenActions(location.pathname);

  const routePermission =
    screenActions.resolved
      ? undefined
      : permission ??
        (role === 'org'
          ? (resolveModuleAdminRoutePermission(location.pathname) ??
            resolveOrgRoutePermission(location.pathname))
          : role === 'any'
            ? POS_ROUTE_PERMISSIONS[location.pathname]
            : undefined);

  const perms = user.permissions ?? [];
  const owner = isOrgOwner(perms, user.roleCode);
  const scopedModule: ErpModule | null =
    !user.masterAdmin && role === 'org'
      ? owner
        ? (resolveModuleFromPath(location.pathname) ?? entryModule ?? null)
        : (entryModule ?? null)
      : null;

  const accessDenial = useMemo(() => {
    if (user.masterAdmin) return null;

    if (role === 'org' && !isLockedModulePath(location.pathname)) {
      return evaluateOrgRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        roleCode: user.roleCode,
        masterAdmin: false,
        entryModule,
        scopedModule,
        routePermission,
        backendCanView: screenActions.resolved ? screenActions.canView : undefined,
        hasFeature,
        featuresLoading,
        accessibleModules,
      });
    }

    if (role === 'any') {
      return evaluatePosRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        masterAdmin: false,
        routePermission,
        hasFeature,
        featuresLoading,
        posRouteFeatures: POS_ROUTE_FEATURES,
        entryModule,
      });
    }

    return null;
  }, [
    user.masterAdmin,
    user.roleCode,
    role,
    location.pathname,
    perms,
    entryModule,
    scopedModule,
    routePermission,
    screenActions.resolved,
    screenActions.canView,
    hasFeature,
    featuresLoading,
    accessibleModules,
  ]);

  const launcherCodes = useMemo(
    () =>
      getLauncherModules(enabledModules, perms, accessibleModules, user.roleCode, has).map(
        (entry) => entry.erpModule,
      ),
    [enabledModules, perms, accessibleModules, user.roleCode, has],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) => {
      if (user.masterAdmin || pathname.startsWith('/master')) return true;
      return canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules);
    },
    [accessibleModules, enabledModules, launcherCodes, user.masterAdmin],
  );

  const fallbackPath = entryModule
    ? getPostLoginPath(entryModule, APP_HOME_FALLBACK, {
        permissions: perms,
        accessibleModules,
        roleCode: user.roleCode,
      })
    : resolveOwnerHomePath(enabledModules);

  const extraRevertCandidates = useMemo(() => {
    if (isHrEssOnlyUser(perms, accessibleModules, user.roleCode)) {
      return [HR_ESS_HOME_PATH];
    }
    return undefined;
  }, [perms, accessibleModules, user.roleCode]);

  return (
    <RouteAccessGate
      denied={accessDenial != null}
      message={accessDenial?.message}
      fallbackPath={accessDenial?.fallbackPath ?? fallbackPath}
      silent={accessDenial?.silent}
      canAccessPath={canAccessPath}
      extraRevertCandidates={extraRevertCandidates}
    />
  );
}

export function ProtectedRoute({ role = 'any', permission }: ProtectedRouteProps) {
  const { isAuthenticated, user, entryModule, initialized, enabledModules, accessibleModules } =
    useAppSelector((s) => s.auth);
  const sessionLoading = useAppSelector((s) => s.session.loading);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const sessionRestoring = isSessionRestorePending(
    initialized,
    sessionLoading,
    sessionLoaded,
    isAuthenticated,
  );
  const location = useLocation();
  const activeModule = useActiveModule();
  const { has } = usePermissions();

  if (sessionRestoring) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const loginPath = resolveLoginRedirect(location.pathname, entryModule, activeModule);
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (role === 'master' && !user.masterAdmin) {
    return <Navigate to="/master/login" replace />;
  }

  if (role === 'org' && user.masterAdmin) {
    return <Navigate to="/master" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    !(user.permissions ?? []).includes('ORG_MANAGE')
  ) {
    return (
      <Navigate
        to={resolveAccessibleLandingPath(
          {
            permissions: user.permissions ?? [],
            roleCode: user.roleCode,
            enabledModules,
            accessibleModules,
            entryModule,
            hasPermission: has,
          },
          activeModule ?? undefined,
        )}
        replace
      />
    );
  }

  if (isPlatformOnboardingRequired() && location.pathname === '/onboarding/pos') {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    (user.permissions ?? []).includes('ORG_MANAGE') &&
    isPlatformOnboardingRequired()
  ) {
    if (isFattahOneProductSelectionRequired() && location.pathname !== '/onboarding/products') {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (
      isPlatformOnboardingRequired() &&
      isProductSelectionDone() &&
      location.pathname !== '/onboarding' &&
      location.pathname !== '/onboarding/pos'
    ) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (isOnboardingDone() && location.pathname.startsWith('/onboarding')) {
    return (
      <Navigate
        to={resolveAccessibleLandingPath(
          {
            permissions: user?.permissions ?? [],
            roleCode: user?.roleCode,
            enabledModules,
            accessibleModules,
            entryModule,
            hasPermission: undefined,
          },
          entryModule ?? undefined,
        )}
        replace
      />
    );
  }

  return <ProtectedRouteOutlet role={role} permission={permission} user={user} entryModule={entryModule} />;
}

export function PublicOnlyRoute() {
  const location = useLocation();
  const { isAuthenticated, user, entryModule, workspaceModule, enabledModules, accessibleModules } =
    useAppSelector((s) => s.auth);
  const { has } = usePermissions();
  const permissions = user?.permissions ?? [];
  if (isAuthenticated && user) {
    const moduleLoginEntry = getModuleEntryByLoginPath(location.pathname);
    if (moduleLoginEntry) {
      const activeModule = entryModule ?? workspaceModule;
      if (activeModule === moduleLoginEntry.erpModule) {
        return <Navigate to={moduleLoginEntry.homePath} replace />;
      }
      // Already signed in to another app — allow product login page to switch workspace.
      return <Outlet />;
    }

    if (user.masterAdmin) {
      return <Navigate to="/master" replace />;
    }
    if (entryModule) {
      return (
        <Navigate
          to={getPostLoginPath(entryModule, resolveOwnerHomePath(enabledModules), {
            permissions,
            accessibleModules,
            roleCode: user.roleCode,
          })}
          replace
        />
      );
    }
    if (isFattahOneProductSelectionRequired()) {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (isPlatformOnboardingRequired()) {
      return <Navigate to="/onboarding" replace />;
    }
    const soleHome = resolveSingleAppHomePath(
      enabledModules,
      has,
      permissions,
      accessibleModules,
      user.roleCode,
    );
    if (soleHome) {
      return <Navigate to={soleHome} replace />;
    }
    if ((user.permissions ?? []).includes('ORG_MANAGE')) {
      return (
        <Navigate
          to={resolveAccessibleLandingPath(
            {
              permissions,
              roleCode: user.roleCode,
              enabledModules,
              accessibleModules,
              entryModule,
            },
            entryModule ?? workspaceModule,
          )}
          replace
        />
      );
    }
    return (
      <Navigate
        to={resolveAccessibleLandingPath(
          {
            permissions,
            roleCode: user.roleCode,
            enabledModules,
            accessibleModules,
            entryModule,
            hasPermission: has,
          },
          workspaceModule ?? entryModule ?? undefined,
        )}
        replace
      />
    );
  }
  return <Outlet />;
}

